import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useWindows } from '../../contexts/WindowContext';
import { useSound } from '../../contexts/SoundContext';
import { Icon } from '../Icons';
import type { WindowConfig } from '../../types';
import styles from './TaskbarItem.module.css';

interface TaskbarItemProps {
  window: WindowConfig;
}

export const TaskbarItem: React.FC<TaskbarItemProps> = ({ window }) => {
  const { focusWindow, minimizeWindow } = useWindows();
  const { playSound } = useSound();
  const [showPreview, setShowPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClick = () => {
    if (window.isActive && window.state !== 'minimized') {
      minimizeWindow(window.id);
      playSound('minimize');
    } else {
      focusWindow(window.id);
      playSound('click');
    }
  };

  const handleMouseEnter = useCallback(() => {
    // Delay showing preview
    hoverTimeoutRef.current = setTimeout(() => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPreviewPosition({
          x: rect.left + rect.width / 2,
          y: rect.top - 10,
        });
        setShowPreview(true);
      }
    }, 400);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setShowPreview(false);
  }, []);

  // Preview thumbnail dimensions
  const previewWidth = 200;
  const previewHeight = 150;

  // Calculate scaled size maintaining aspect ratio
  const getPreviewDimensions = () => {
    const aspectRatio = window.size.width / window.size.height;
    let width = previewWidth;
    let height = previewHeight;

    if (aspectRatio > previewWidth / previewHeight) {
      height = previewWidth / aspectRatio;
    } else {
      width = previewHeight * aspectRatio;
    }

    return { width, height };
  };

  const previewDims = getPreviewDimensions();

  return (
    <>
      <button
        ref={buttonRef}
        className={`${styles.taskbarItem} ${window.isActive && window.state !== 'minimized' ? styles.active : ''}`}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        title={window.title}
        aria-label={`${window.title}${window.state === 'minimized' ? ' (minimized)' : ''}`}
      >
        <Icon name={window.icon} size={16} className={styles.icon} />
        <span className={styles.title}>{window.title}</span>
      </button>

      {showPreview && createPortal(
        <div
          className={styles.previewContainer}
          style={{
            left: previewPosition.x,
            top: previewPosition.y,
            transform: 'translate(-50%, -100%)',
          }}
          onMouseEnter={() => setShowPreview(true)}
          onMouseLeave={handleMouseLeave}
        >
          <div className={styles.previewWindow}>
            {/* Title bar */}
            <div className={styles.previewTitleBar}>
              <Icon name={window.icon} size={12} />
              <span className={styles.previewTitle}>{window.title}</span>
            </div>
            {/* Content preview */}
            <div
              className={styles.previewContent}
              style={{
                width: previewDims.width,
                height: previewDims.height,
              }}
            >
              {/* Show app icon as placeholder */}
              <div className={styles.previewPlaceholder}>
                <Icon name={window.icon} size={48} />
                <span>{window.title}</span>
              </div>
            </div>
          </div>
          <div className={styles.previewArrow} />
        </div>,
        document.body
      )}
    </>
  );
};

export default TaskbarItem;
